import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { MenuList } from '@wikifactory/voxel-ui';
import { Menu, MenuItem } from 'components';
import { Link } from 'core/routing';
import { getProfileMenus } from 'config/menu';
import { useClientStore, useRouterStore } from 'apps/hooks';
import { MoreIcon } from 'icons';
import { themes } from 'styles/themes/wikifactoryNew';
import { DefaultMenuRoute } from 'constants/global';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';
import { FrameSidebarWidth, SubHeaderHeight } from 'styles/constants';
import { useDesktopCompatibility } from 'hooks/useDesktopCompatible';
import FooterMenu from './FooterMenu';
import { Profile, SignOut } from '../icons';
import { InitiativeCard } from './InitiativeCard';
import { MenuHeader } from './MenuHeader';

import { ProfileAvatar } from './ProfileAvatar';

export type FrameSidebarProps = {
  isSidebarOpen?: boolean;
  shouldShowFrameSideBar?: boolean;
};

export const FrameSidebar = observer(() => {
  const routerStore = useRouterStore();
  const clientStore = useClientStore();
  const { route, navigate } = routerStore;

  const { signedInProfile } = clientStore;
  const { fullName, profileSlug } = signedInProfile;

  const compatibleWithTab = useDesktopCompatibility({
    feature: 'tab',
  });

  const [avatarEl, setAvatarEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (['workspace', 'home'].includes(route.name)) {
      navigate(DefaultMenuRoute, {
        spacePath: clientStore.slugFromRouteOrSpace,
      });
    }
  }, [route.name, clientStore.slugFromRouteOrSpace]);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarEl(avatarEl ? null : event.currentTarget);
  };

  const closeProfileMenu = () => {
    setAvatarEl(null);
  };

  const desktopMode = isInDesktopMode();

  const subHeaderHeight =
    desktopMode && compatibleWithTab ? 0 : SubHeaderHeight;

  return (
    <div>
      <div
        className={`sticky p-1 ${
          desktopMode && compatibleWithTab ? 'top-0' : 'mt-8 top-8'
        } left-1`}
        style={{
          height: `calc(100vh - ${subHeaderHeight}px)`,
          overflowY: 'auto',
          borderRight: `1px solid ${themes.color.wfGrey100}`,
          width: FrameSidebarWidth,
        }}
      >
        <div
          className='rounded'
          style={{
            border: `1px solid ${themes.color.wfGrey100}`,
          }}
        >
          <MenuHeader
            avatar={<ProfileAvatar />}
            name={fullName}
            isProfile
            rightIcon={<MoreIcon />}
            onClickRightIcon={handleAvatarClick}
            className='cursor-default'
            iconClassName='hover:opacity-60'
          />
          <MenuList items={getProfileMenus()} />
        </div>
        <InitiativeCard />
        <FooterMenu />
      </div>
      <Menu
        open={Boolean(avatarEl)}
        anchorEl={avatarEl}
        onClose={closeProfileMenu}
        onClick={closeProfileMenu}
        placement='customize-position'
        customizePosition={({ top, height, right, modalStyle }) => ({
          left: `calc(${right - 5}px - ${modalStyle.borderWidth})`,
          top: `calc(${top + height + 5}px - ${modalStyle.borderWidth} + ${
            window.scrollY
          }px)`,
        })}
      >
        <Link name='profile' params={{ profile: profileSlug }} openInBrowser>
          <MenuItem>
            <div className='mr-1'>
              <Profile />
            </div>
            Community Profile
          </MenuItem>
        </Link>
        <MenuItem
          data-testid='top-menu-sign-out'
          onClick={() => {
            clientStore.SignOutUser();
          }}
        >
          <div className='mr-1'>
            <SignOut />
          </div>
          Log Out
        </MenuItem>
      </Menu>
    </div>
  );
});
